/**
 * Accessibility mitigation for the Yotpo "See Reviews Summary" slide-out.
 *
 * Yotpo's widget opens the summary panel (.yotpo-reviews-summary-modal-backdrop
 * > .yotpo-reviews-summary-container) without moving keyboard focus into it,
 * without dialog semantics, and only exposes an <svg> "close summary" control
 * that isn't keyboard-operable. This is vendor markup the theme can't edit at
 * the source, so we patch it at runtime:
 *   - add role="dialog" / aria-modal / aria-label / tabindex="-1"
 *   - move focus into the panel when it opens
 *   - trap Tab within the panel and close it on Escape
 *   - make the close control keyboard-operable
 *   - restore focus to the trigger button when it closes
 *
 * If/when Yotpo fixes this upstream, this file can be removed.
 */
(function () {
  if (window.__yotpoSummaryA11yInit) return;
  window.__yotpoSummaryA11yInit = true;

  var BACKDROP = '.yotpo-reviews-summary-modal-backdrop';
  var CONTAINER = '.yotpo-reviews-summary-container';
  var TRIGGER = 'button.yotpo-icon-btn, button[class*="yotpo"]';
  var LABEL = window.__yotpoSummaryLabel || 'Reviews summary';

  var lastTrigger = null;

  // Remember which control opened the summary so focus can return there.
  document.addEventListener(
    'click',
    function (event) {
      var target = event.target;
      if (!target || !target.closest) return;
      var btn = target.closest(TRIGGER);
      if (btn && /summary/i.test(btn.textContent || '')) lastTrigger = btn;
    },
    true
  );

  function getFocusable(container) {
    var nodes = container.querySelectorAll(
      'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
    );
    return Array.prototype.filter.call(nodes, function (el) {
      return el.offsetParent !== null;
    });
  }

  function findClose(scope) {
    return scope.querySelector(
      '[aria-label="close summary"], [aria-label*="close" i], [class*="close"]'
    );
  }

  function closeModal(container) {
    var scope = container.closest(BACKDROP) || container;
    var close = findClose(scope);
    if (close) {
      if (typeof close.click === 'function') close.click();
      else close.dispatchEvent(new MouseEvent('click', { bubbles: true }));
    }
  }

  function activate(container) {
    if (container.__a11yReady) return;
    container.__a11yReady = true;

    var scope = container.closest(BACKDROP) || container;

    if (!container.getAttribute('role')) container.setAttribute('role', 'dialog');
    container.setAttribute('aria-modal', 'true');
    if (!container.getAttribute('aria-label') && !container.getAttribute('aria-labelledby')) {
      container.setAttribute('aria-label', LABEL);
    }
    container.setAttribute('tabindex', '-1');

    // Make Yotpo's <svg> close control keyboard-operable.
    var close = findClose(scope);
    if (close && !close.__a11yKey) {
      close.__a11yKey = true;
      if (!close.getAttribute('role')) close.setAttribute('role', 'button');
      if (close.getAttribute('tabindex') === null) close.setAttribute('tabindex', '0');
      close.addEventListener('keydown', function (event) {
        if (event.key === 'Enter' || event.key === ' ' || event.key === 'Spacebar') {
          event.preventDefault();
          closeModal(container);
        }
      });
    }

    // Move focus into the dialog once it's in the DOM.
    requestAnimationFrame(function () {
      try {
        container.focus();
      } catch (e) {}
    });

    function onKeydown(event) {
      if (!document.contains(container)) return;
      if (event.key === 'Escape') {
        closeModal(container);
        return;
      }
      if (event.key !== 'Tab') return;
      var focusable = getFocusable(container);
      if (!focusable.length) {
        event.preventDefault();
        container.focus();
        return;
      }
      var first = focusable[0];
      var last = focusable[focusable.length - 1];
      var active = document.activeElement;
      if (event.shiftKey && (active === first || active === container)) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && active === last) {
        event.preventDefault();
        first.focus();
      }
    }

    container.__onKeydown = onKeydown;
    document.addEventListener('keydown', onKeydown, true);
  }

  function deactivate(container) {
    if (container && container.__onKeydown) {
      document.removeEventListener('keydown', container.__onKeydown, true);
      container.__onKeydown = null;
    }
    if (lastTrigger && document.contains(lastTrigger)) {
      try {
        lastTrigger.focus();
      } catch (e) {}
    }
    lastTrigger = null;
  }

  function findContainer(node) {
    if (node.nodeType !== 1) return null;
    if (node.matches && node.matches(CONTAINER)) return node;
    if (node.querySelector) return node.querySelector(CONTAINER);
    return null;
  }

  function isModalNode(node) {
    if (node.nodeType !== 1) return false;
    if (node.matches && (node.matches(CONTAINER) || node.matches(BACKDROP))) return true;
    return !!(node.querySelector && node.querySelector(CONTAINER));
  }

  var observer = new MutationObserver(function (mutations) {
    mutations.forEach(function (mutation) {
      Array.prototype.forEach.call(mutation.addedNodes || [], function (node) {
        var container = findContainer(node);
        if (container) activate(container);
      });
      Array.prototype.forEach.call(mutation.removedNodes || [], function (node) {
        if (isModalNode(node)) deactivate(findContainer(node));
      });
    });
  });

  observer.observe(document.body, { childList: true, subtree: true });
})();
